import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { allMangaList } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = allMangaList.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
        <div className="max-w-7xl mx-auto text-center py-20">
          <div className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-300">
            <i className="ri-file-unknow-line text-4xl"></i>
          </div>
          <p className="mt-4 text-foreground-500">漫画未找到</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const mockChapters = Array.from({ length: Math.min(manga.totalChapters, 20) }, (_, i) => ({
    id: i + 1,
    title: `第${i + 1}话 ${i === 0 ? '序章' : i === manga.totalChapters - 1 ? '最终话' : ''}`,
    number: i + 1,
    updateDate: new Date(new Date(manga.updateTime).getTime() - (manga.totalChapters - i - 1) * 86400000 * 3).toISOString().split('T')[0],
  }));

  const relatedManga = allMangaList
    .filter((m) => m.id !== manga.id)
    .filter((m) => m.category === manga.category || m.tags.some((t) => manga.tags.includes(t)))
    .slice(0, 4);

  const categoryLabels: Record<string, string> = {
    korean: '韩漫全集',
    chinese: '国漫全集',
    japanese: '日漫完结',
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${manga.title} - 咻咻漫画`,
          "description": manga.description,
          "author": { "@type": "Person", "name": manga.author },
          "datePublished": "2025-01-01",
          "dateModified": manga.updateTime,
          "inLanguage": "zh-CN",
          "publisher": { "@type": "Organization", "name": "咻咻漫画" }
        })}
      </script>

      <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: categoryLabels[manga.category] || '漫画', path: `/category/${manga.category}` },
              { label: manga.title },
            ]}
          />

          {/* Manga Detail */}
          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full sm:w-[220px] md:w-[260px]">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={manga.cover}
                  alt={`${manga.title} - 全集漫画免费阅读`}
                  title={`${manga.title} | 咻咻漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">{manga.title}</h1>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-foreground-400"></i>
                  <strong>作者：</strong>{manga.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line text-foreground-400"></i>
                  <strong>分类：</strong>
                  <Link to={`/category/${manga.category}`} className="text-primary-500 hover:text-primary-600 cursor-pointer">
                    {categoryLabels[manga.category]}
                  </Link>
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  manga.status === 'completed'
                    ? 'bg-accent-100 text-accent-900'
                    : 'bg-secondary-100 text-secondary-900'
                }`}>
                  {manga.status === 'completed' ? '已完结' : '连载中'}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-yellow-500"></i>
                  <strong>{manga.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-foreground-400"></i>
                  <strong>{(manga.views / 10000).toFixed(1)}万</strong> 次阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-3-line text-foreground-400"></i>
                  共 <strong>{manga.totalChapters}</strong> 话
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-foreground-400"></i>
                  更新于 {manga.updateTime}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-fill mr-1.5"></i>开始阅读
                </button>
                <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line mr-1.5"></i>加入收藏
                </button>
              </div>

              <div className="mt-5">
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <div className="mt-10">
            <h2 className="text-lg font-heading text-foreground-900 mb-4 flex items-center gap-2">
              <i className="ri-list-check text-primary-500"></i>章节列表
            </h2>
            <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200/70">
                {mockChapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/manga/${manga.id}/chapter/${ch.number}`}
                    className="flex items-center justify-between px-4 py-3 bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700 truncate">{ch.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.updateDate}</span>
                  </Link>
                ))}
              </div>
              {manga.totalChapters > 20 && (
                <div className="text-center py-3 border-t border-background-200/70">
                  <button className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                    展开全部 {manga.totalChapters} 话 <i className="ri-arrow-down-s-line ml-1"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Related Manga */}
          {relatedManga.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-heading text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-primary-500"></i>相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedManga.map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}